import * as Sentry from '@sentry/angular';
import { enableProdMode, LOCALE_ID, importProvidersFrom, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AppComponent } from './app/app.component';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { TROVATA_APP_STATES } from './app/core/models/state.model';
import { AuthenticationModule } from './app/core/modules/authentication.module';
import { appRoutes } from './app/app-routing';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { TmsMessageInterceptor } from './app/features/tms/services/tms-message.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideLottieOptions } from 'ngx-lottie';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { logoutPlugin } from './app/core/store/plugins/logout.plugin';
import { provideStore, withNgxsPlugin } from '@ngxs/store';
import { ENVIRONMENT_TOKEN } from './app/core/models/config.model';
import { HEAP_INITIALIZER_PROVIDER } from './app/core/services/heap-analytics.service';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MY_LUXON_FORMATS } from './app/shared/utils/datepicker-formats';
import { provideRouter, Router, withRouterConfig } from '@angular/router';
import { provideHighcharts } from 'highcharts-angular';
// Sankey series-type module is eagerly registered here (static import in the
// `main` bundle) so it's available synchronously before any chart paints.
// Lazy registration via providePartialHighcharts.modules can lose the race on
// first route entry, producing Highcharts error #17 (missingModuleFor: sankey)
// and an empty diagram.
import 'highcharts/esm/modules/sankey';
import 'highcharts/esm/modules/treemap';
import { AuthState } from './app/core/store/state/auth/auth.state';
import { PreferencesState } from './app/shared/store/state/preferences/preferences.state';
import { IdentityState } from './app/features/settings/store/state/identity.state';
import { TQLFieldsState } from './app/features/tql/store/state/tql-fields/tql-fields.state';
import { FilterObjectState } from './app/shared/store/state/filter-object/filter-object.state';
import { CurrencyState } from './app/shared/store/state/currency/currency.state';
import { SENTRY_DSN } from '../sentry.config';
import { ChunkErrorHandler } from './app/core/services/chunk-error-handler.service';
import { normalizeLocale } from './app/shared/utils/locale-utils';
import { AGENTS_DATA_PROVIDER } from './app/features/ai-agents/services/agents-data-provider';
import { AgentsDataProviderRouter } from './app/features/ai-agents/services/agents-data-provider.router';
import { AGENTS_AI_PROVIDER } from './app/features/ai-agents/services/agents-ai-provider';
import { AgentsAiProviderRouter } from './app/features/ai-agents/services/agents-ai-provider.router';
import { ARTIFACTS_DATA_PROVIDER } from './app/features/ai-artifacts/services/artifacts-data-provider';
import { ArtifactsDataProviderRouter } from './app/features/ai-artifacts/services/artifacts-data-provider.router';
import { SUGGESTIONS_PROVIDER } from './app/features/trovata-ai/suggestions/services/suggestions-provider';
import { SuggestionsProviderRouter } from './app/features/trovata-ai/suggestions/services/suggestions-provider.router';

Sentry.init({
	dsn: SENTRY_DSN,
	integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
	environment: environment.environmentName,
	enableLogs: true,
	sendDefaultPii: true,
	ignoreErrors: [
		// Ignore specific errors
		'ExpressionChangedAfterItHasBeenCheckedError',
		// Ignore chunk loading errors (these occur when users have cached old app versions)
		'ChunkLoadError',
		'Loading chunk',
		/error loading dynamically imported module/i,
		/Failed to fetch dynamically imported module/i,
		/Loading CSS chunk/i,
	],
	beforeSend(event, hint) {
		// Additional filtering for chunk loading errors that might slip through ignoreErrors
		const error = hint.originalException;
		if (error && typeof error === 'object') {
			const errorMessage = (error as Error).message || String(error);
			// Check if it's a chunk loading error
			if (
				errorMessage.includes('dynamically imported module') ||
				errorMessage.includes('ChunkLoadError') ||
				errorMessage.includes('Loading chunk') ||
				errorMessage.includes('Failed to fetch')
			) {
				return null; // Don't send this event to Sentry
			}
		}
		return event;
	},
});

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, {
	providers: [
		// 'computed' restores history on a cancelled popstate (e.g. a DirtyGuard refusing Back) instead of replacing the entry
		provideRouter(appRoutes, withRouterConfig({ canceledNavigationResolution: 'computed' })),

		// Http + animations
		provideHttpClient(withInterceptorsFromDi()),
		provideAnimations(),

		// TMS message-system SSoT: one interceptor records every app's AppData
		// save messages into the shared history popover (capture rule for all
		// app types). withInterceptorsFromDi() above picks up HTTP_INTERCEPTORS.
		{ provide: HTTP_INTERCEPTORS, useClass: TmsMessageInterceptor, multi: true },

		// NGXS store + plugins (devtools LAST)
		provideStore(
			TROVATA_APP_STATES,
			{ selectorOptions: { suppressErrors: true } },
			withNgxsStoragePlugin({
				keys: [
					AuthState,
					CurrencyState,
					'customerFeature.userAccessConfig',
					'customerFeature.featurePermissions',
					'customerFeature.configVersion',
					FilterObjectState,
					IdentityState,
					PreferencesState,
					TQLFieldsState,
					'transactions.highVolumeTransactionMode',
					'core.connectionCount',
				],
			}),
			withNgxsPlugin(logoutPlugin),
			withNgxsReduxDevtoolsPlugin({ disabled: environment.production })
		),

		// App/env tokens
		{ provide: ENVIRONMENT_TOKEN, useValue: environment },
		{ provide: LOCALE_ID, useValue: normalizeLocale(navigator.language) },

		// AI Agents data seam — resolves to the runtime-routing provider, which
		// delegates to the Cash provider by default and the TMS provider on /tms
		// routes. AgentsState is a root singleton, so routing is at runtime, not a
		// route-level override.
		{ provide: AGENTS_DATA_PROVIDER, useExisting: AgentsDataProviderRouter },
		{ provide: AGENTS_AI_PROVIDER, useExisting: AgentsAiProviderRouter },

		// AI Data Artifacts data seam (TMS-only) — resolves to the runtime-routing provider,
		// which delegates to an empty Cash no-op by default and the TMS provider on /tms routes.
		{ provide: ARTIFACTS_DATA_PROVIDER, useExisting: ArtifactsDataProviderRouter },

		// HomeAI suggestion feed data seam — resolves to the runtime-routing provider, which
		// returns an empty feed for Cash (no network) and the ATOM home-suggestions feed for TMS.
		{ provide: SUGGESTIONS_PROVIDER, useExisting: SuggestionsProviderRouter },

		// --- Sentry (Angular) ---
		{
			provide: ErrorHandler,
			useFactory: () => new ChunkErrorHandler(Sentry.createErrorHandler()),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: APP_INITIALIZER,
			// ensures TraceService is instantiated early (for routing instrumentation)
			useFactory: () => () => {},
			deps: [Sentry.TraceService],
			multi: true,
		},
		// --- end Sentry ---

		// Heap analytics (loads per-environment app ID, starts autocapture)
		HEAP_INITIALIZER_PROVIDER,

		// 3rd-party providers
		provideLottieOptions({ player: () => import('lottie-web') }),
		provideEnvironmentNgxMask(),
		provideHighcharts(),

		// Only import NgModules that actually contribute providers
		importProvidersFrom(AuthenticationModule, MatSnackBarModule, MatNativeDateModule),

		// Material date adapters
		provideLuxonDateAdapter(),
		{ provide: MAT_DATE_FORMATS, useValue: MY_LUXON_FORMATS },
		{ provide: MAT_DATE_LOCALE, useValue: 'en-US' },
	],
}).catch(err => console.error(err));
